import React, { useEffect } from 'react'
import { Button, Card, Form } from 'react-bootstrap'
import { AnnotationDataBoundingBox } from '../../../types/models/annotation'
import SnippetItem from '../../snippets/SnippetItem'
import { useAppSelector } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { useGetProjectQuery } from '../../../services/projectsApi'
import PillsClassification from '../../PillsClassification'
import { Tag } from '../../../types/models/tag'
import { useGetTagsQuery } from '../../../services/tagApi'
import { Bot } from 'lucide-react'

interface SnippetListProps {
  items: AnnotationDataBoundingBox[]
  contentId: string
  selectedBBoxClassIndex?: number | undefined
  showConfidence?: boolean
  showAcceptButton?: boolean
  showRejectButton?: boolean
  onAccept?: (box: AnnotationDataBoundingBox, e: React.MouseEvent | KeyboardEvent) => void
  onReject?: (box: AnnotationDataBoundingBox, e: React.MouseEvent | KeyboardEvent) => void
  hoveredBoxIndex?: number | undefined
  onHover?: (index: number | undefined) => void
  onSelect?: (box: AnnotationDataBoundingBox, index: number) => void
  // Selection support (used in AnnotationsReview export flow)
  selectionMode?: boolean
  selectedIndices?: number[]
  onToggleSelect?: (index: number) => void
  rotateLongSideVertical?: boolean
}

const SnippetList: React.FC<SnippetListProps> = ({
  items,
  contentId,
  selectedBBoxClassIndex,
  showConfidence = true,
  showAcceptButton = false,
  showRejectButton = false,
  onAccept,
  onReject,
  hoveredBoxIndex,
  onHover,
  onSelect,
  selectionMode = false,
  selectedIndices = [],
  onToggleSelect,
  rotateLongSideVertical = false,
}) => {
  const projectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(projectId || '', {
    skip: !projectId,
  })

  const { data: tags } = useGetTagsQuery(
    {
      project_id: projectId || '',
      dataset_id: project?.datasetid || '',
    },
    { skip: !projectId || !project?.datasetid }
  )

  useEffect(() => {
    if (selectedBBoxClassIndex !== undefined && selectedBBoxClassIndex !== null) {
      const el = document.getElementById(`prediction-snippet-${selectedBBoxClassIndex}`)
      if (el && typeof el.scrollIntoView === 'function') {
        try {
          el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        } catch {
          el.scrollIntoView()
        }
      }
    }
  }, [selectedBBoxClassIndex])

  const isBBox =
    project?.annotation_type === 'bounding_box' ||
    project?.annotation_type === 'oriented_bounding_box'

  return (
    <div className="d-flex flex-column">
      {items.map((box, index) => {
        const isSelected = selectedBBoxClassIndex === box.class_index
        const isChecked = selectedIndices?.includes(index)

        return (
          <Card
            key={box.class_index}
            id={`prediction-snippet-${box.class_index}`}
            className="bg-transparent border-0"
          >
            <Card.Body
              className="p-0"
              onClick={() => {
                onSelect && onSelect(box, index)
              }}
              style={{ cursor: 'pointer' }}
            >
              <div className="d-flex flex-column flex-sm-row align-items-center gap-2">
                {selectionMode && (
                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{ minWidth: '28px' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Form.Check
                      type="checkbox"
                      checked={!!isChecked}
                      onChange={() => onToggleSelect && onToggleSelect(index)}
                      aria-label="Select snippet"
                    />
                  </div>
                )}
                <div className="flex-grow-1 w-100">
                  {isBBox ? (
                    <SnippetItem
                      contentId={contentId}
                      bbox={box}
                      isSelected={isSelected}
                      rotateLongSideVertical={rotateLongSideVertical}
                      tagNames={[
                        tags?.tags?.find(
                          (tag: Tag) => tag.id === box.class_name || tag.id === box.name
                        )?.name || '',
                      ]}
                      tagConfidence={showConfidence ? [box.confidence || 0] : undefined}
                      projectAnnotationType={project?.annotation_type || ''}
                    />
                  ) : (
                    <PillsClassification
                      tagNames={[box.class_name || '']}
                      tagConfidence={showConfidence ? [box.confidence || 0] : undefined}
                      maxTags={1}
                      large={true}
                    />
                  )}
                </div>
                {((showAcceptButton && onAccept) || (showRejectButton && onReject)) && (
                  <div
                    className="d-flex flex-column gap-2 mt-2 mt-sm-0"
                    style={{ minWidth: '80px' }}
                  >
                    {(isBBox || project?.annotation_type === 'classification') &&
                      box?.confidence !== undefined &&
                      box.confidence !== null && (
                        <div className="px-2 py-1 rounded bg-dark text-light d-flex align-items-center gap-1">
                          <Bot size={14} />
                          <span className="small">AI</span>
                        </div>
                      )}
                    {showAcceptButton && onAccept && (
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          onAccept(box, e)
                        }}
                        className="flex-grow-1 w-100"
                      >
                        Accept
                      </Button>
                    )}
                    {showRejectButton && onReject && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          onReject(box, e)
                        }}
                        className="flex-grow-1 w-100"
                      >
                        Reject
                      </Button>
                    )}
                  </div>
                )}
              </div>
              {/* For classification projects, the pill is rendered inline above instead of here */}
            </Card.Body>
          </Card>
        )
      })}
    </div>
  )
}

export default SnippetList
